import { ReactNode, useEffect, useRef, useState } from 'react';
import { useInViewport } from 'hooks/useInViewport';

import styles from './RibbonSlides.module.scss';

/**
 * The first switch comes 2 s after the slot is on screen, so a player sees that
 * it rotates; after that each slide holds 10 s (EN-11155, Ryan 10/27).
 */
export const FIRST_SLIDE_MS = 2_000;
export const SLIDE_MS = 10_000;

interface RibbonSlidesProps {
  slides: { key: string; label: string; node: ReactNode }[];
  /** Sideways on phone and tablet, downward in the desktop header. */
  direction: 'x' | 'y';
  /** Reduce animations: switch with a cut, not a slide. */
  still: boolean;
}

/**
 * The promo slot holding two ribbons (EN-11155): the first switch 2 s after the
 * slot is on screen, then each holds 10 s while the whole ribbon leaves and the
 * other arrives; a dot per slide switches at once. One timer, paused while the
 * slot is scrolled away or the tab is hidden, and restarted from a full 10 s
 * after a dot tap.
 */
export default function RibbonSlides({ slides, direction, still }: RibbonSlidesProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInViewport(ref);
  const [index, setIndex] = useState(0);
  const [pageVisible, setPageVisible] = useState(true);
  // The quick first switch happens once; a dot tap counts as the player having seen it.
  const [switched, setSwitched] = useState(false);
  const count = slides.length;

  useEffect(() => {
    const onChange = () => setPageVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [count, index]);

  useEffect(() => {
    if (count < 2 || !inView || !pageVisible) return undefined;
    const id = window.setTimeout(
      () => {
        setSwitched(true);
        setIndex((i) => (i + 1) % count);
      },
      switched ? SLIDE_MS : FIRST_SLIDE_MS
    );
    return () => window.clearTimeout(id);
  }, [count, inView, pageVisible, index, switched]);

  if (count === 1) return <>{slides[0].node}</>;

  return (
    <div className={`${styles.wrap} ${direction === 'y' ? styles.header : ''}`}>
      <div
        ref={ref}
        className={`${styles.viewport} ${direction === 'y' ? styles.vertical : ''} ${
          still ? styles.still : ''
        }`}>
        {slides.map((slide, i) => {
          const state = i === index ? styles.active : i < index ? styles.before : styles.after;
          return (
            <div
              key={slide.key}
              className={`${styles.slide} ${state}`}
              aria-hidden={i !== index}
              // An off-screen slide must not take focus or taps.
              {...(i !== index ? { inert: '' } : {})}>
              {slide.node}
            </div>
          );
        })}
      </div>
      <div className={styles.dots} role='tablist' aria-label='Lobby ribbons'>
        {slides.map((slide, i) => (
          <button
            key={slide.key}
            type='button'
            role='tab'
            aria-selected={i === index}
            aria-label={slide.label}
            className={`${styles.dot} ${i === index ? styles.dotOn : ''}`}
            onClick={() => {
              setSwitched(true);
              setIndex(i);
            }}
          />
        ))}
      </div>
    </div>
  );
}
